import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { getModalOffset } from '../components/ModalRegion/utils';

interface UseUpdateEditorSizeProps {
	editorViewElement: HTMLElement;
	/**
	 * Comment editors (e.g. Jira description): while AI streams, never let the editor plus the
	 * height div shrink, and re-sync on editor resize and modal moves as well as modal resize.
	 * Replace-mode streams rewrite text above the content that follows the editor, so without
	 * this the Save/Cancel row and everything below it move up and down on every chunk.
	 */
	holdHeightDuringStreaming?: boolean;
	isInDocumentStreamingEnabled?: boolean;
	/** Whether AI is streaming into the editor. Only read with `holdHeightDuringStreaming`. */
	isStreaming?: boolean;
	modalElement: HTMLElement | null;
	/**
	 * Current modal top. With `holdHeightDuringStreaming`, the height is re-synced when it
	 * changes, because moving the modal does not resize it.
	 */
	modalTop?: number;
}

export const HEIGHT_ELEMENT_CLASS_NAME = 'ai-height-div';

const syncHeightToModal = ({
	heightDiv,
	modalElement,
	modalHeight,
	editorViewElement,
	heldTotalHeightRef,
	isHolding,
}: {
	editorViewElement: HTMLElement;
	heightDiv: HTMLDivElement | null;
	heldTotalHeightRef: MutableRefObject<number | null>;
	isHolding: boolean;
	modalElement: HTMLElement;
	modalHeight: number;
}): void => {
	if (!heightDiv) {
		return;
	}
	const editorHeight = editorViewElement.offsetHeight;

	if (isHolding) {
		// Measure the editor in the coordinates the modal is positioned in: offsets up to the
		// modal's offset parent. This does not depend on the two sharing an offset parent, and
		// unlike viewport coordinates it ignores scrolling. Jira's add-comment editor scrolls
		// inside its own box while the modal is positioned outside it, so a viewport measurement
		// would count each scroll as extra height and the hold would keep it.
		// `offsetHeight` is used on every hold path so two measurements never disagree.
		const editorTop = getModalOffset({ from: editorViewElement, modalElement, direction: 'top' });
		const modalBottom = modalElement.offsetTop + modalElement.offsetHeight;
		// Box height = editor plus the space below it that the modal needs. It is recorded when the
		// stream starts and only ever grows, so the spacer shrinks exactly as the editor grows.
		const naturalTotalHeight = Math.max(modalBottom - editorTop, editorHeight);
		const heldTotalHeight = Math.max(
			naturalTotalHeight,
			heldTotalHeightRef.current ?? naturalTotalHeight,
		);
		heldTotalHeightRef.current = heldTotalHeight;
		heightDiv.style.height = `${Math.max(heldTotalHeight - editorHeight, 0)}px`;
		return;
	}

	heldTotalHeightRef.current = null;
	const modalBottom = modalElement.offsetTop + modalHeight;
	const editorBottom = editorViewElement.offsetTop + editorHeight;

	// The height of the extra div should be expanded to reach
	// the bottom of the modal -- so that no modal content is outside
	// the editor
	const height = Math.max(modalBottom - editorBottom, 0);

	heightDiv.style.height = `${height}px`;
};

/**
 * whenever we update modal size - we want to update editor size to accommodate for it
 * This hook listens for modal size changes, and updates editor content area height accordingly
 */
export function useUpdateEditorHeight({
	modalElement,
	editorViewElement,
	isInDocumentStreamingEnabled,
	isStreaming = false,
	modalTop,
	holdHeightDuringStreaming = false,
}: UseUpdateEditorSizeProps): void {
	const heightDivRef = useRef<HTMLDivElement | null>(null);
	// Tallest editor + height div seen during the current stream; null when not holding.
	const heldTotalHeightRef = useRef<number | null>(null);

	// Each stream holds its own height. Reset when a stream starts so a second stream in the same
	// session does not inherit the previous stream's height. Declared before the syncing effects
	// so the reset runs first.
	useEffect(() => {
		if (holdHeightDuringStreaming && isStreaming) {
			heldTotalHeightRef.current = null;
		}
	}, [holdHeightDuringStreaming, isStreaming]);

	// listen to changes to modal size
	useEffect(() => {
		if (!modalElement) {
			return;
		}

		const resizeObserver = new ResizeObserver((entries) => {
			const modalEntry = entries.find((e) => e.target === modalElement);
			if (!modalEntry && !holdHeightDuringStreaming) {
				return;
			}
			syncHeightToModal({
				heightDiv: heightDivRef.current,
				modalElement,
				modalHeight: modalEntry ? modalEntry.contentRect.height : modalElement.offsetHeight,
				editorViewElement,
				heldTotalHeightRef,
				isHolding: holdHeightDuringStreaming && isStreaming,
			});
		});

		resizeObserver.observe(modalElement);
		if (holdHeightDuringStreaming) {
			resizeObserver.observe(editorViewElement);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [modalElement, editorViewElement, holdHeightDuringStreaming, isStreaming]);

	useEffect(() => {
		if (!holdHeightDuringStreaming || !modalElement) {
			return;
		}
		syncHeightToModal({
			heightDiv: heightDivRef.current,
			modalElement,
			modalHeight: modalElement.offsetHeight,
			editorViewElement,
			heldTotalHeightRef,
			isHolding: isStreaming,
		});
	}, [modalTop, modalElement, editorViewElement, holdHeightDuringStreaming, isStreaming]);

	// Create an element for adjusting height of editor when modal is visible
	useEffect(() => {
		if (heightDivRef.current) {
			// In future react will fire useEffect twice in dev mode
			// this early exit avoids issues with this
			return;
		}
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const newDiv = doc.createElement('div');
		newDiv.className = HEIGHT_ELEMENT_CLASS_NAME;
		newDiv.style.height = `0px`;

		if (isInDocumentStreamingEnabled) {
			// Position directly after the editor element to avoid
			// selector ambiguity in nested editor scenarios.
			editorViewElement.after(newDiv);
		} else {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			editorViewElement.parentNode!.appendChild(newDiv);
		}
		heightDivRef.current = newDiv;

		return () => {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				newDiv.parentNode?.removeChild(newDiv);
			} else {
				editorViewElement.parentNode?.removeChild(newDiv);
			}
		};
	}, [editorViewElement, isInDocumentStreamingEnabled]);
}
